import React, {useEffect, useRef} from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { TouchBackend } from 'react-dnd-touch-backend';

import UpDownButtonPanel from "../buttons/buttonPanels/MoveUpMoveDownPanel";
import DragButton from "../buttons/DragButton";
import {IContentListItem, IContentOrderSetter} from "../../../interfaces/content/content";




const DraggableItem = ({ item, index, moveItem, onMoveUp, onMoveDown, isFirst, isLast }: IContentListItem) => {
    const ref = useRef<HTMLLIElement>(null);

    const [{ isDragging }, drag] = useDrag({
        type: "CONTENT_ITEM",
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: () => {},
    });

    const [, drop] = useDrop({
        accept: "CONTENT_ITEM",
        hover(dragged: { index: number }) {
            if (dragged.index === index) return;

            const rect = ref.current?.getBoundingClientRect();
            if (!rect) return;

            moveItem(dragged.index, index);
            dragged.index = index; // mutate to keep in sync
        },
    });

    drag(drop(ref));

    useEffect(() => {
        if (isDragging) {
            document.body.classList.add('is-dragging-cursor');
        } else {
            document.body.classList.remove('is-dragging-cursor');
        }
        return () => document.body.classList.remove('is-dragging-cursor');
    }, [isDragging]);


    return (
        <li ref={ref} className={`content-item`}>
            <span className="left-panel" draggable={false}>
                <DragButton dragRef={undefined} className="content-drag-button" />
                <span className="content-title">{item.title}</span>
            </span>
            <UpDownButtonPanel
                panelClassName="right-panel"
                ariaLabelUp="Move Content Up"
                ariaLabelDown="Move Content Down"
                upDisabled={isFirst}
                downDisabled={isLast}
                onUpClick={onMoveUp}
                onDownClick={onMoveDown}
            />
        </li>
    );
};

const ContentOrderSetter = ({ content, setContent }: IContentOrderSetter) => {
    const moveItem = (from: number, to: number) => {
        const newContent = [...content];
        const [dragged] = newContent.splice(from, 1);
        newContent.splice(to, 0, dragged);
        setContent(newContent);
    };

    const moveContentItemUp = (index: number) => {
        if (index === 0) return;
        const newContent = [...content];
        [newContent[index - 1], newContent[index]] = [newContent[index], newContent[index - 1]];
        setContent(newContent);
    };

    const moveContentItemDown = (index: number) => {
        if (index === content.length - 1) return;
        const newContent = [...content];
        [newContent[index], newContent[index + 1]] = [newContent[index + 1], newContent[index]];
        setContent(newContent);
    };

    return (
        <DndProvider backend={TouchBackend} options={{enableMouseEvents: true}}>
            <ul className="content-order-setter-container">
                {content.map((item, index) => (
                    <DraggableItem
                        key={item.id}
                        item={item}
                        index={index}
                        moveItem={moveItem}
                        onMoveUp={() => moveContentItemUp(index)}
                        onMoveDown={() => moveContentItemDown(index)}
                        isFirst={index === 0}
                        isLast={index === content.length - 1}
                    />
                ))}
            </ul>
        </DndProvider>
    );
};

export default ContentOrderSetter;